import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Route, Routes } from 'react-router-dom';
import { App } from './App';
import { AuthProvider } from './lib/auth';
import { PortalApp } from './pages/Portal';
import { PharmacyApp } from './pages/pharmacy/PharmacyApp';
import './index.css';

/**
 * A short stale window keeps clinical data fresh without hammering the API.
 * Retries are off for mutations: re-posting a verification or a report
 * generation is never the right recovery.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: { retry: 0 },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/*
          The patient portal is mounted OUTSIDE <AuthProvider> on purpose.
          AuthProvider is the clinic session, it reads the clinic token,
          applies the tenant theme and decides staff routing. A patient must
          never pass through it, and this placement means they cannot: there is
          no code path where a portal screen has a clinic session in scope.
        */}
        <Routes>
          <Route path="/portal/*" element={<PortalApp />} />
          {/* The pharmacy portal (S-15 to S-19), outside the clinic session for the same reason. */}
          <Route path="/pharmacy/*" element={<PharmacyApp />} />
          <Route
            path="*"
            element={
              <AuthProvider>
                <App />
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
